import React, { useState, useEffect, useRef } from "react";
import {
  formatDateTime,
  isSameDay,
  useDecodedTokenResult,
  convertTo12Hour,
} from "../../lib/utils";
import { db } from "../../config/firebaseConfig";
import { ref, push, set, onValue, get, update } from "firebase/database";
import { ArrowLeftIcon, CheckIcon, SendIcon, VideoIcon } from "lucide-react";
import { VideoUploader } from "../../components/Video/VideoUploader";
import { GrAttachment } from "react-icons/gr";

interface ChatMessage {
  text: string;
  timestamp: number;
  customerName?: string;
  sender: "vendor" | "customer";
  receiver: "vendor" | "customer";
  readByVendor: boolean;
  readByCustomer: boolean;
  filePath?: string;
}

interface ChatScreenProps {
  customerId: string;
  onBack?: () => void;
  showBackButton?: boolean;
}

const ChatScreen: React.FC<ChatScreenProps> = ({
  customerId,
  onBack,
  showBackButton,
}) => {
  const { vendorId } = useDecodedTokenResult();
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [customerName, setCustomerName] = useState<string>("");
  const [messages, setMessages] = useState<(ChatMessage & { id: string })[]>(
    []
  );
  const chatId = `${vendorId}_${customerId}`;
  const [showVideoUploader, setShowVideoUploader] = useState(false);
  const [isUploadDisabled, setIsUploadDisabled] = useState(false);
  const [videoDetails, setVideoDetails] = useState({
    title: "",
    filePath: "",
  });

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
  };

  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages]);

  useEffect(() => {
    scrollToBottom();
  }, [customerId]);

  useEffect(() => {
    const chatRef = ref(db, `chats/${chatId}`);
    const unsubscribe = onValue(chatRef, (snapshot) => {
      if (snapshot.exists()) {
        const chatData = snapshot.val();
        setCustomerName(chatData.customerName || "");
      }
    });

    return () => unsubscribe();
  }, [chatId]);

  useEffect(() => {
    const chatRef = ref(db, `chats/${chatId}/messages`);
    const unsubscribe = onValue(
      chatRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const messagesData = snapshot.val();
          const messagesList = Object.entries(messagesData).map(
            ([id, data]) => ({
              id,
              ...(data as ChatMessage),
            })
          );
          messagesList.sort((a, b) => a.timestamp - b.timestamp);
          setMessages(messagesList);
        } else {
          setMessages([]);
        }
      },
      (error) => {
        setError("Failed to load messages: " + error.message);
      }
    );

    return () => unsubscribe();
  }, [chatId]);

  useEffect(() => {
    const markMessagesAsRead = async () => {
      const chatRef = ref(db, `chats/${chatId}/messages`);
      const snapshot = await get(chatRef);

      if (snapshot.exists()) {
        const updates: { [key: string]: any } = {};
        Object.entries(snapshot.val()).forEach(
          ([messageId, message]: [string, any]) => {
            if (message.sender === "customer" && !message.readByVendor) {
              updates[`${messageId}/readByVendor`] = true;
            }
          }
        );

        if (Object.keys(updates).length > 0) {
          await update(ref(db, `chats/${chatId}/messages`), updates);
        }
      }
    };

    markMessagesAsRead();
  }, [chatId]);

  useEffect(() => {
    setShowVideoUploader(false);
    // setVideoDetails({ title: "", filePath: "" });
  }, [customerId]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    try {
      if (!message.trim() && !videoDetails.filePath) return;

      const newMessage: ChatMessage = {
        text: message,
        timestamp: Date.now(),
        sender: "vendor",
        receiver: "customer",
        readByVendor: false,
        readByCustomer: false,
        ...(videoDetails.filePath && { filePath: videoDetails.filePath }),
      };

      const chatRef = ref(db, `chats/${chatId}/messages`);
      await push(chatRef, newMessage);
      setMessage("");
      setVideoDetails({ title: "", filePath: "" });
      setShowVideoUploader(false);
    } catch (err) {
      setError("Failed to send message: " + (err as Error).message);
    }
  };

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="border-b px-4 py-2 shadow-sm">
        <div className="flex items-center space-x-3">
          {showBackButton && (
            <button
              onClick={onBack}
              className="md:hidden p-1 rounded-full hover:bg-gray-100"
            >
              <ArrowLeftIcon className="w-6 h-6 text-gray-600" />
            </button>
          )}
          <div className="w-8 h-8 rounded-full bg-customGreenColor flex items-center justify-center">
            <span className="text-white font-medium">
              {customerName?.slice(0, 1).toUpperCase() || "?"}
            </span>
          </div>
          <h2 className="text-lg font-semibold text-gray-800">
            {customerName || `Customer #${customerId.slice(0, 8)}`}
          </h2>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3">
        {messages.map((msg, index) => (
          <div key={msg.id} className="mb-3">
            {/* Date separator - show for first message and when date changes */}
            {(index === 0 ||
              !isSameDay(msg.timestamp, messages[index - 1].timestamp)) && (
              <div className="flex justify-center mb-4">
                <div className="bg-gray-100 rounded-full px-4 py-1">
                  <span className="text-xs text-gray-500">
                    {formatDateTime(msg.timestamp, "DD/MM/YYYY")}
                  </span>
                </div>
              </div>
            )}

            <div
              className={`flex ${
                msg.sender === "vendor" ? "justify-end" : "justify-start"
              }`}
            >
              <div
                className={`max-w-[75%] ${
                  msg.sender === "vendor" ? "items-end" : "items-start"
                }`}
              >
                {msg.filePath ? (
                  <div className="mb-2">
                    {msg.filePath.includes("videos") ? (
                      <video
                        src={msg.filePath}
                        controls
                        className="rounded-lg max-w-full"
                        style={{ maxHeight: "200px" }}
                      >
                        Your browser does not support the video tag.
                      </video>
                    ) : (
                      <img
                        src={msg.filePath}
                        alt="Shared image"
                        className="rounded-lg max-w-full"
                        style={{ maxHeight: "200px" }}
                      />
                    )}
                  </div>
                ) : null}
                {msg.text && (
                  <div
                    className={`rounded-2xl px-4 py-2 ${
                      msg.sender === "vendor"
                        ? "bg-customGreenColor text-white rounded-br-sm"
                        : "bg-gray-100 text-gray-800 rounded-bl-sm"
                    }`}
                  >
                    <p className="text-sm">{msg.text}</p>
                  </div>
                )}
                <div
                  className={`flex items-center mt-1 text-xs text-gray-500 ${
                    msg.sender === "vendor" ? "justify-end" : "justify-start"
                  }`}
                >
                  <span>
                    {convertTo12Hour(formatDateTime(msg.timestamp, "HH:mm"))}
                  </span>
                  {msg.sender === "vendor" && (
                    <span className="ml-1">
                      {msg.readByCustomer ? (
                        <>
                          <CheckIcon className="w-3.5 h-3.5 text-blue-400" />
                        </>
                      ) : (
                        <CheckIcon className="w-3.5 h-3.5 text-gray-400" />
                      )}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Add this div at the bottom of the messages container */}
        <div ref={messagesEndRef} />
      </div>

      {showVideoUploader && (
        <div className="border-t border-gray-200 p-4">
          <VideoUploader
            setIsDisabled={setIsUploadDisabled}
            setVideoDetails={setVideoDetails}
            isForEditVideo={false}
            filePath={videoDetails.filePath}
            imageAllowed={true}
            urlUseS3={true}
            isForChat={true}
          />
        </div>
      )}

      <div className="border-t px-4 py-3">
        <form onSubmit={handleSendMessage} className="flex space-x-2">
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => setShowVideoUploader(!showVideoUploader)}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <GrAttachment className="w-5 h-5 text-gray-600" />
            </button>
          </div>
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="flex-1 border border-gray-300 rounded-full px-4 py-2 text-sm focus:outline-none focus:border-customGreenColor"
            placeholder="Type a message..."
          />
          <button
            type="submit"
            disabled={
              (!message.trim() && !videoDetails.filePath) || isUploadDisabled
            }
            className="bg-customGreenColor text-white rounded-full p-2 hover:bg-customGreenColor disabled:opacity-50"
          >
            <SendIcon className="w-5 h-5" />
          </button>
        </form>
      </div>
    </div>
  );
};

// Helper function to check if two timestamps are from the same day

export default ChatScreen;
